/** Web-safe raster extensions — never run HEIC conversion on these names. */
function hasWebImageExtension(file: File): boolean {
  return /\.(jpe?g|png|gif|webp|bmp|tif|tiff|avif)$/i.test(file.name);
}

/** iPhone / Apple photos (often empty MIME on desktop file pickers). */
export function isHeicOrHeifFile(file: File): boolean {
  if (hasWebImageExtension(file)) return false;
  if (/\.(heic|heif)$/i.test(file.name)) return true;
  const t = (file.type || "").toLowerCase();
  return (
    t === "image/heic" ||
    t === "image/heif" ||
    t === "image/heic-sequence" ||
    t === "image/heif-sequence"
  );
}

export function isLikelyImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|tif|tiff|avif)$/i.test(file.name);
}

/**
 * Convert HEIC/HEIF to JPEG for upload and browser preview.
 * Returns the original file when already web-safe.
 */
/** True when the bytes are HEIC/HEIF whatever the file is called (e.g. a renamed .heic → .png). */
async function hasHeicSignature(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    const text = String.fromCharCode(...head);
    return text.slice(4, 8) === "ftyp" && /^(heic|heix|hevc|hevx|heim|heis|mif1|msf1)/.test(text.slice(8, 12));
  } catch {
    return false;
  }
}

export async function prepareImageFileForUpload(file: File): Promise<File> {
  if (!isHeicOrHeifFile(file) && !(await hasHeicSignature(file))) return file;

  let blob: Blob | undefined;
  try {
    // Newer libheif: reads current iPhone HEIC files.
    const { heicTo } = await import("heic-to");
    blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.92 });
  } catch {
    try {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
      blob = Array.isArray(converted) ? converted[0] : converted;
    } catch {
      blob = undefined;
    }
  }
  if (!(blob instanceof Blob)) {
    throw new Error("Couldn't read this HEIC photo. Set the iPhone camera to Most Compatible, or export it as JPEG.");
  }

  const baseName = file.name.replace(/\.[a-z0-9]+$/i, "") || "photo";
  return new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

/** Object URL safe for <img> preview (converts HEIC first). */
export async function createImagePreviewObjectUrl(file: File): Promise<string> {
  const prepared = await prepareImageFileForUpload(file);
  return URL.createObjectURL(prepared);
}
